import { forwardRef } from 'react';

import { mergeClasses } from '@/utils';

import { IInput } from './Input.interfaces';

const Input = forwardRef<HTMLInputElement, IInput>(
  (
    {
      className,
      type,
      prefix,
      postfix,
      prefixStyle,
      postfixStyle,
      label,
      error,
      required,
      ...props
    },
    ref,
  ) => {
    return (
      <div className={mergeClasses('flex flex-col gap-1.5 border-neutral-n4 w-full', className)}>
        {label && (
          <label className="text-sm font-semibold text-neutral-n9">
            {label}
            {required && <span className="ml-1 text-semantic-error-base">*</span>}
          </label>
        )}

        <div className="relative flex items-center">
          {prefix && (
            <div
              className={mergeClasses(
                'pointer-events-none absolute left-5 flex items-center text-neutral-n7',
                prefixStyle,
              )}
            >
              {prefix}
            </div>
          )}

          <input
            ref={ref}
            type={type}
            required={required}
            className={mergeClasses(
              'h-10 w-full min-w-0 rounded-lg border border-neutral-n4 bg-white px-4 text-body-sm font-medium text-brand-primary-darkest outline-none',
              'placeholder:text-neutral-n6 selection:bg-primary selection:text-primary-foreground',
              'focus-visible:border-brand-primary-base',
              'disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-neutral-n2 disabled:border-neutral-n4 disabled:text-neutral-n6 disabled:font-normal',
              prefix && 'pl-11',
              postfix && 'pr-11',
              error && '!border-semantic-error-base',
            )}
            {...props}
          />

          {postfix && (
            <div
              className={mergeClasses(
                'pointer-events-none absolute right-5 flex items-center text-neutral-n7',
                postfixStyle,
              )}
            >
              {postfix}
            </div>
          )}
        </div>

        {error && <p className="text-xs text-semantic-error-base">{error}</p>}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
